import type { PurchasesPackage } from 'react-native-purchases'
import { useCallback, useReducer } from 'react'
import { Platform } from 'react-native'
import { useAppSession } from '@/src/contexts/AppSessionContext'
import { useContentfulLocale } from '@/src/hooks/useContentfulLocale'
import { appQueryOptions } from '@/src/queries/view-queries'
import { useSuspenseQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'

import { shouldUseCompactPurchaseLayout } from '../utils/compactPurchaseLayout'
import { getPaymentScreenCopy } from '../utils/purchaseScreenCopy'
import {
  initialPurchaseScreenState,
  purchaseScreenReducer,
} from '../utils/purchaseScreenState'
import { usePurchaseFlow } from './usePurchaseFlow'
import { getPurchaseOfferingsLocaleState } from './usePurchaseOfferings'
import { usePurchasePricing } from './usePurchasePricing'
import { usePurchaseScreenAnimation } from './usePurchaseScreenAnimation'
import { useTrackPaywallViewed } from './useTrackPaywallViewed'
import { getBrowserUserAgent, useViewportHeights } from './useViewportHeight'

export function usePurchaseScreenController() {
  const {
    purchase,
    logout,
    backendUser,
    revenueCatInitStatus,
    isRevenueCatTestStore,
    isLoading: isSessionLoading,
    retryRevenueCatInitialization,
  } = useAppSession()
  const [state, dispatch] = useReducer(
    purchaseScreenReducer,
    initialPurchaseScreenState,
  )
  const { t } = useTranslation(['common', 'purchase', 'profile', 'setup'])

  const language = useContentfulLocale()
  const { isSwedish, targetCurrency } =
    getPurchaseOfferingsLocaleState(language)
  const { formatCurrency, yearlySavingsFormatted } = usePurchasePricing({
    packages: state.packages,
    language,
    isSwedish,
    targetCurrency,
  })
  const { data: app } = useSuspenseQuery(appQueryOptions(language))
  const paymentView = app.onboardingPaymentStep

  const { visualViewportHeight, windowHeight } = useViewportHeights()
  const isCompactLayout = shouldUseCompactPurchaseLayout({
    platform: Platform.OS,
    userAgent: getBrowserUserAgent(),
    viewportHeight: visualViewportHeight,
  })
  const layoutViewportHeight = isCompactLayout
    ? visualViewportHeight
    : windowHeight
  const isPurchaseScreenVisible =
    !isSessionLoading &&
    revenueCatInitStatus !== 'idle' &&
    revenueCatInitStatus !== 'initializing' &&
    revenueCatInitStatus !== 'identifying'
  const { cardAnimatedStyle, heroHeight } = usePurchaseScreenAnimation({
    cardHeight: state.cardHeight,
    isCompactLayout,
    isVisible: isPurchaseScreenVisible,
    windowHeight: layoutViewportHeight,
  })
  const { paywallOpenTimeRef } = useTrackPaywallViewed({
    hasResolvedOptions: state.hasResolvedOptions,
    isVisible: isPurchaseScreenVisible,
    packageCount: state.packages.length,
    selectedPackageIdentifier: state.selectedPackage?.identifier ?? null,
  })
  const copy = getPaymentScreenCopy({
    paymentView,
    t: (key, options) => t(key as never, options as never) as unknown as string,
    yearlySavingsFormatted,
  })

  const handleOptionsChange = useCallback(
    ({
      packages,
      selectedPackage,
    }: {
      packages: PurchasesPackage[]
      selectedPackage: PurchasesPackage | null
    }) => {
      dispatch({ type: 'setOptions', packages, selectedPackage })
    },
    [],
  )
  const { handlePurchase, isProcessing, purchaseMutation } = usePurchaseFlow({
    dispatch,
    isLoggingOut: state.isLoggingOut,
    logout,
    openTimeRef: paywallOpenTimeRef,
    purchase,
    selectedPackage: state.selectedPackage,
  })

  return {
    backendUser,
    cardAnimatedStyle,
    copy,
    dispatch,
    formatCurrency,
    handleOptionsChange,
    handlePurchase,
    heroHeight,
    isCompactLayout,
    isProcessing,
    isPurchaseScreenVisible,
    isRevenueCatTestStore,
    isSessionLoading,
    language,
    labels: {
      skip: t('common:skip'),
      pageTitle: t('common:page_title_purchase'),
      testStoreBadge: t('purchase:test_store_badge'),
      voucher: t('purchase:voucher_button'),
    },
    purchaseMutation,
    retryRevenueCatInitialization,
    revenueCatInitStatus,
    state,
  }
}
